import { useSeo } from '../lib/seo';
/**
 * Accommodation page — separate listing of stays (hotels, cottages, lodges)
 * filtered server-side from the attractions collection by category='accommodation'.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BedDouble, MapPin } from 'lucide-react';
import api from '../lib/api';

export default function AccommodationPage() {
  useSeo(
    'Places to Stay in Leicestershire | Go Leicestershire',
    'Hotels, aparthotels and self-catering stays across Leicestershire — with accessibility details and great-value Stay, Play & Explore break packages.'
  );
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/accommodation').then((r) => setData(r.data)); }, []);
  if (!data) return <div className="py-32 text-center text-muted">Loading...</div>;
  return (
    <div>
      <Helmet>
        <title>Stay over · GoLeicestershire</title>
        <meta name="description" content="Hotels, cottages and country breaks across Leicestershire — directly from the people who run them." />
      </Helmet>
      <section className="bg-heritage text-base py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-3 inline-flex items-center gap-2"><BedDouble size={14}/> Stay over</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-6 max-w-3xl">Make it a weekend, not a day trip.</h1>
          <p className="text-base/85 text-lg max-w-2xl">A selection of our accommodation partners — {data.total} places to stay across Leicestershire.</p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="accommodation-partner-links">
            <a href="https://stayplayexplore.co.uk" target="_blank" rel="noopener noreferrer"
               data-testid="link-stayplayexplore-family"
               className="inline-flex items-center gap-1.5 bg-base text-heritage font-semibold rounded-full px-5 py-2.5 hover:bg-mustard transition-colors">
              Family breaks at Stay Play Explore <ArrowUpRight size={14}/>
            </a>
            <a href="https://www.theperfectbreak.co.uk/" target="_blank" rel="noopener noreferrer"
               data-testid="link-perfectbreak-couples"
               className="inline-flex items-center gap-1.5 border border-base/30 text-base font-semibold rounded-full px-5 py-2.5 hover:bg-base/10 transition-colors">
              Adults-only breaks at The Perfect Break <ArrowUpRight size={14}/>
            </a>
          </div>
        </div>
      </section>
      <section className="py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {data.items.length === 0 ? (
            <div className="text-center text-muted py-20">No accommodation listings yet. Check back soon.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {data.items.map((a) => (
                <Link key={a.id} to={`/explore/${a.slug}`} data-testid={`stay-card-${a.slug}`} className="group bg-white border border-charcoal/10 rounded-3xl overflow-hidden hover:shadow-soft hover:-translate-y-1 transition-all block">
                  <div className="aspect-[4/3] overflow-hidden bg-stone">
                    {a.hero_image && <img src={a.hero_image} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-5">
                    {a.town && <div className="text-muted text-xs uppercase tracking-widest mb-2 inline-flex items-center gap-1.5"><MapPin size={11}/> {a.town}</div>}
                    <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-heritage transition-colors">{a.name}</h3>
                    <p className="text-muted text-sm line-clamp-2 mb-3">{a.summary || a.description?.slice(0, 130)}</p>
                    <span className="text-heritage font-semibold text-sm inline-flex items-center gap-1">See stay <ArrowUpRight size={13}/></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
